import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Profession.css";

interface ProfessionItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Profession: React.FC = () => {
  const [data, setData] = useState<ProfessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/profession`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching professions:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: ProfessionItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: ProfessionItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Professions",
    de: "Berufe",
    ta: "தொழில்கள்",
    si: "වෘත්තීන්",
    hi: "पेशे",
    ar: "المهن",
    tr: "Meslekler",
    ru: "Профессии",
    zh: "职业",
    fr: "Professions",
    es: "Profesiones",
  };

  const subtitleMap = {
    en: "Professions in German",
    de: "Berufe auf Deutsch",
    ta: "ஜெர்மன் மொழியில் தொழில்கள்",
    si: "ජර්මන් භාෂාවෙන් වෘත්තීන්",
    hi: "जर्मन में पेशे",
    ar: "المهن بالألمانية",
    tr: "Almanca'da Meslekler",
    ru: "Профессии на немецком",
    zh: "德语职业",
    fr: "Professions en allemand",
    es: "Profesiones en alemán",
  };

  return (
    <div className="profession-page-container">
      <div className="profession-page-header">
        <div>
          <h1 className="profession-page-title">{titleMap["de"]}</h1>
          <p className="profession-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="profession-page-loading">Loading...</div>
      ) : (
        <div className="profession-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`profession-page-card ${
                  isPlaying ? "profession-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="profession-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="profession-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="profession-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="profession-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="profession-page-sentence-text">
                  {item.sentence}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Profession;
